import React, { useState, useMemo } from "react";
import { Search, Plus, Download, FileSpreadsheet, FileText, Pencil, Trash2, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { exportCSV, exportXLSX } from "../lib/exportUtils";
import { toast } from "sonner";
import { applyFilter, useHSE } from "../lib/store";

export default function RegisterPage({
  title,
  subtitle,
  storeKey,
  columns,
  formFields,
  idPrefix,
  badgeMap = {},
  toolbarExtras,
  topPanels,
  onSaveRecord,
}) {
  const { data, setData, filters, setFilters } = useHSE();
  const rows = data[storeKey] || [];

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = applyFilter(rows, { ...filters, search: filters.search || search }, columns.find(c => c.key === "date") ? "date" : (columns.find(c => c.key === "dateReceived") ? "dateReceived" : "date"));
    return list;
  }, [rows, filters, search, columns]);

  const addNew = () => {
    const blank = formFields.reduce((a, f) => ({ ...a, [f.key]: f.default ?? "" }), {});
    blank.id = `${idPrefix}-${Date.now().toString().slice(-6)}`;
    setEditing(blank);
    setOpen(true);
  };

  const save = async () => {
    const exists = rows.find((r) => r.id === editing.id);

    if (typeof onSaveRecord === "function") {
      try {
        await onSaveRecord(editing, { exists, rows, storeKey });
      } catch (error) {
        console.warn("Backend save unavailable, keeping local fallback data.", error);
        toast.warning("Backend unavailable - saved locally");
      }
    }

    const next = exists
      ? rows.map((r) => (r.id === editing.id ? editing : r))
      : [editing, ...rows];

    setData({ ...data, [storeKey]: next });
    setOpen(false);

    if (typeof onSaveRecord === "function") {
      toast.success(exists ? "Record updated" : "Record submitted");
    } else {
      toast.success(exists ? "Record updated" : "Record added");
    }
  };

  const remove = (id) => {
    if (!confirm("Delete this record?")) return;
    setData({ ...data, [storeKey]: rows.filter((r) => r.id !== id) });
    toast.success("Record deleted");
  };

  const renderCell = (col, row) => {
    if (col.render) return col.render(row);
    const val = row[col.key];
    if (badgeMap[col.key]) {
      const m = badgeMap[col.key][val] || { bg: "#F1F5F9", color: "#455560" };
      return (
        <span className="cmd-pill" style={{ background: m.bg, color: m.color }}>
          {val}
        </span>
      );
    }
    return val;
  };

  return (
    <div className="space-y-5" data-testid={`${storeKey}-page`}>
      {/* Title bar */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-[#0E2336]">{title}</h1>
          {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              data-testid={`${storeKey}-search`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Searchâ€¦"
              className="text-xs bg-transparent focus:outline-none w-44"
            />
          </div>
          <Button data-testid={`${storeKey}-export-csv`} variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
            onClick={() => { exportCSV(filtered, `${storeKey}.csv`); toast.success("CSV exported"); }}>
            <FileText className="w-3.5 h-3.5" /> CSV
          </Button>
          <Button data-testid={`${storeKey}-export-xlsx`} variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
            onClick={() => { exportXLSX(filtered, `${storeKey}.xlsx`, title); toast.success("Excel exported"); }}>
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </Button>
          {toolbarExtras}
          <Button data-testid={`${storeKey}-add`} onClick={addNew} size="sm"
            className="h-8 gap-1.5 text-xs font-semibold" style={{ background: "#183C55", color: "white" }}>
            <Plus className="w-3.5 h-3.5" /> Add New
          </Button>
        </div>
      </div>

      {topPanels}

      {/* Table */}
      <div className="command-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead style={{ background: "#183C55" }}>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider text-white font-semibold whitespace-nowrap">{c.label}</th>
                ))}
                <th className="text-right py-2.5 px-3 text-[10px] uppercase tracking-wider text-white font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="py-12 text-center text-slate-400 text-sm">No records found</td></tr>
              )}
              {filtered.map((r, i) => (
                <tr key={r.id} className={`border-b border-slate-100 hover:bg-amber-50/40 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="py-2 px-3 text-slate-700 whitespace-nowrap">{renderCell(c, r)}</td>
                  ))}
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button data-testid={`${storeKey}-edit-${r.id}`} className="text-slate-500 hover:text-[#183C55] p-1" onClick={() => { setEditing(r); setOpen(true); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`${storeKey}-delete-${r.id}`} className="text-slate-500 hover:text-rose-500 p-1" onClick={() => remove(r.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-slate-100 text-[11px] text-slate-500 flex justify-between">
          <span>{filtered.length} of {rows.length} records</span>
          <span className="font-mono">Linked to dashboard</span>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing && rows.find(r => r.id === editing.id) ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {formFields.map((f) => (
                <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{f.label}</label>
                  {f.type === "select" ? (
                    <select
                      data-testid={`field-${f.key}`}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white"
                    >
                      <option value="">â€”</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "textarea" ? (
                    <textarea
                      data-testid={`field-${f.key}`}
                      rows={3}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm"
                    />
                  ) : (
                    <Input
                      data-testid={`field-${f.key}`}
                      type={f.type || "text"}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="mt-1 h-9 text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="dialog-save-btn" onClick={save} style={{ background: "#183C55", color: "white" }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Pill = ({ value, type }) => {
  const map = type || {};
  const m = map[value] || { bg: "#F1F5F9", color: "#455560" };
  return <span className="cmd-pill" style={{ background: m.bg, color: m.color }}>{value}</span>;
};

